import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Select
} from "@mui/material";
import { DataGrid, GridToolbarContainer, GridFooter } from "@mui/x-data-grid";
import bkstApi from "../../common/component/api";
import { Link as RouterLink } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { anyWordStartsWith, capitalize, getItemMetadata, equalsIgnoreCase } from "../../common/component/util";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { IMG_PLACEHOLDER, STORAGE_BUCKET } from "../../common/component/constants";
import currency from "currency.js";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import ItemFilter from "../../order/component/ItemFilter";
import { useNavigate } from "react-router";

const FILTERS = ["active", "draft"];

// Custom footer component that appears at the top
const CustomFooterOnTop = ({ selectedRows, onBulkAction }) => {
  return (
    <GridToolbarContainer>
      {selectedRows && selectedRows.length > 0 && (
        <Box pl={1}>
          <Select variant="standard" size="small" value="" displayEmpty onChange={(e) => onBulkAction(e.target.value)} sx={{ minWidth: 120 }}>
            <MenuItem value="" disabled>
              Bulk Actions
            </MenuItem>
            <MenuItem value="campaign">Send Campaign</MenuItem>

            {/* <MenuItem value="activate">Activate</MenuItem>
            <MenuItem value="deactivate">Deactivate</MenuItem>*/}
          </Select>
        </Box>
      )}
      <GridFooter sx={{ border: "none", flexGrow: 1 }} />
    </GridToolbarContainer>
  );
};

// Actions cell component with menu
const ActionsCell = ({ product, onProductUpdate, siteId }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /*const toggleStatus = () => {
    const newStatus = product.status === "ACTIVE" ? "DRAFT" : "ACTIVE";

    bkstApi
      .put(`/my/product/${product.id}`, { status: newStatus })
      .then((res) => {
        onProductUpdate(product.id, { status: newStatus });
        handleClose();
      })
      .catch((error) => {
        // Do Nothing
      });
  };*/

  return (
    <>
      <IconButton size="small" onClick={handleClick} title="More options">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        {/*<MenuItem sx={{ padding: "20px" }} onClick={toggleStatus}>
          <PowerSettingsNewIcon sx={{ mr: 1, fontSize: 20 }} />
          {product.status === "ACTIVE" ? "Deactivate" : "Activate"}
        </MenuItem>

        <MenuItem
          sx={{ padding: "20px" }}
          component={RouterLink}
          to={`/site/${siteId}/product/add`}
          state={{
            product: { ...product, title: `${product.title} (Copy)` }
          }}
        >
          <ContentCopyIcon sx={{ mr: 1, fontSize: 20 }} />
          Duplicate
        </MenuItem>*/}
        <MenuItem sx={{ padding: "20px" }} component={RouterLink} to={`/site/${siteId}/product/${product.id}`}>
          <EditOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
      </Menu>
    </>
  );
};

// DataGrid columns configuration
const getColumns = (siteId, isMobile = false, handleProductUpdate) => [
  {
    field: "image",
    headerName: "Image",
    width: 80,
    sortable: false,
    renderCell: (params) => (
      <Box sx={{ position: "relative" }}>
        <img
          src={params.row.images?.[0] || `${STORAGE_BUCKET}${IMG_PLACEHOLDER}`}
          style={{
            width: "60px",
            height: "60px",
            objectFit: "cover",
            borderRadius: "4px"
          }}
          alt={params.row.title}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "4px",
            right: "4px",
            width: "12px",
            height: "12px",
            backgroundColor: params.row.status === "ACTIVE" ? "#16a085" : "#c0392b",
            borderRadius: "50%",
            border: "1px solid white",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
          }}
        />
      </Box>
    )
  },
  {
    field: "title",
    headerName: "Item Name",
    flex: 1,
    minWidth: 200,
    renderCell: (params) => {
      // Strip HTML tags and get first 75 characters of description
      const getDescriptionPreview = (description) => {
        if (!description) return "";
        // Remove HTML tags
        const textOnly = description.replace(/<[^>]*>/g, "");
        // Take first 75 characters
        return textOnly.length > 75 ? textOnly.substring(0, 75) + "..." : textOnly;
      };

      return (
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1">{params.value}</Typography>
          {params.row.description && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="textSecondary">
                {getDescriptionPreview(params.row.description)}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="textSecondary">
              {(() => {
                const metadata = getItemMetadata(params.row);
                if (metadata.price.max > metadata.price.min) {
                  return `${currency(metadata.price.min).format()} - ${currency(metadata.price.max).format()}`;
                } else {
                  return currency(metadata.price.min).format();
                }
              })()}
            </Typography>
          </Box>
          {params.row.shopify && (
            <Box>
              <Button component={RouterLink} to={`https://labon.bakestreet.co/product/${params.row.id}`} target="_blank">
                Our
              </Button>
              <Button
                component={RouterLink}
                to={`https://la-bonbonniere-bake-shoppe.myshopify.com/products/${params.row.shopify.handle}`}
                target="_blank"
              >
                Their
              </Button>
            </Box>
          )}
        </Box>
      );
    }
  },
  // Hide category and fields on mobile
  ...(isMobile
    ? []
    : [
        {
          field: "category",
          headerName: "Category",
          width: 120,
          renderCell: (params) => {
            const categories = params.row.category || [params.row.type?.toLowerCase()];
            const sortedCategories = categories?.sort((a, b) => a.localeCompare(b)) || [];
            return (
              <Box>
                {sortedCategories.map((cat, index) => (
                  <Typography key={index} variant="caption" display="block">
                    {capitalize(cat)}
                  </Typography>
                ))}
              </Box>
            );
          }
        },
        {
          field: "field",
          headerName: "Field",
          width: 120,
          renderCell: (params) => {
            const variants = params.row.variants || {};
            const fieldNames = [];

            if (variants) {
              Object.values(variants)
                .sort((v1, v2) => {
                  return v2.rank - v1.rank || v2.name.localeCompare(v1.name);
                })
                .forEach((variant) => {
                  if (variant.name && variant.name.toLowerCase() !== "size") {
                    fieldNames.push(variant.name);
                  }
                });
            }

            return (
              <Box>
                {fieldNames.map((name, index) => (
                  <Typography key={index} variant="caption" display="block">
                    {name}
                  </Typography>
                ))}
              </Box>
            );
          }
        }
      ]),
  {
    field: "channel",
    headerName: "Channel",
    width: 100,
    renderCell: (params) => {
      return (
        <Box>
          {params.row.channel &&
            Object.entries(params.row.channel)
              .filter(([key, channel]) => channel.status === "ACTIVE")
              .map(([key, channel], index) => <Chip key={index} display="block" label={key} size="small" />)}
        </Box>
      );
    }
  },
  {
    field: "actions",
    headerName: "Action",
    width: 80,
    sortable: false,
    renderCell: (params) => <ActionsCell product={params.row} onProductUpdate={handleProductUpdate} siteId={siteId} />
  }
];

export default function OnlineShop(props) {
  let location = useLocation();
  const { siteId } = props;

  const [filter, setFilter] = useState("active");

  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [productFilter, setProductFilter] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    bkstApi(`/admin/site/${siteId}/product?filter=${filter}`).then((res) => {
      let sortedByRank = res.data.products.sort((o1, o2) => {
        return o1.title > o2.title ? 1 : -1;
      });
      setProducts(sortedByRank);
      setFiltered(sortedByRank);

      setLoading(false);
    });
  }, [location.key, filter]);

  const getRank = (cat, arr) => {
    cat = cat.toLowerCase();
    if (cat.includes("featured")) return 10000;

    const rank = arr.reduce((acc, o) => +acc + +o.rank, 0);
    const itemsWithEndDate = arr.filter((o) => o.rules?.date?.max);

    if (itemsWithEndDate.length > 0 && itemsWithEndDate.length / arr.length > 0.4) {
      // if half of items in category have end date, sort by end date
      return 8000;
    } else if (rank > arr.length) {
      // sort by bestsellers
      return rank;
    } else {
      // sort by number of items in category
      return arr.length; // all other strings
    }
  };

  /** Using filtered instead of query since results are not rendered before this is executed */
  // Apply filters to products
  useEffect(() => {
    let filteredProducts = products;

    // Apply text query filter
    if (query) {
      filteredProducts = filteredProducts.filter((o) => anyWordStartsWith(o.title, query));
    }

    // Apply product filters
    if (Object.keys(productFilter).length > 0) {
      filteredProducts = filteredProducts.filter((product) => {
        for (const [key, filterValue] of Object.entries(productFilter)) {
          if (filterValue?.value) {
            if (key === "category") {
              const categories = product.category || [product.type?.toLowerCase()];
              if (!categories.some((cat) => cat.toLowerCase() === filterValue.value.toLowerCase())) {
                return false;
              }
            } else if (key === "channel") {
              let channelMatch = false;
              if (product.channel && typeof product.channel === "object") {
                for (const [channelKey, channelValue] of Object.entries(product.channel)) {
                  if (channelKey === filterValue.value && channelValue && channelValue.status === "ACTIVE") {
                    channelMatch = true;
                    break;
                  }
                }
              }
              if (filterValue.value && !channelMatch) {
                return false;
              }
            } else if (key === "field") {
              if (filterValue.value) {
                console.log(filterValue.value);
                const productVariants = product.variants || {};
                const hasField = Object.values(productVariants).some((variant) => equalsIgnoreCase(variant.name, filterValue.value));
                if (!hasField) {
                  return false;
                }
              }
            }
          }
        }
        return true;
      });
    }

    setFiltered(filteredProducts);
  }, [products, query, productFilter]);

  // Handle bulk actions
  const handleBulkAction = (action) => {
    if (!selectedRows.length) return;

    switch (action) {
      case "activate":
        console.log("Activating products:", selectedRows);
        // TODO: Implement bulk activate
        break;
      case "deactivate":
        console.log("Deactivating products:", selectedRows);
        // TODO: Implement bulk deactivate
        break;
      case "campaign":
        console.log("Build Campaign:", selectedRows);
        // Find full product data for each selected row
        const selectedData = products.filter((p) => selectedRows.includes(p.id));
        // Call /marketing route and pass along the complete selected products in state using react-router navigate
        navigate(`/site/${siteId}/marketing`, { state: { items: selectedData } });
        break;
      default:
        break;
    }
  };

  // Handle product updates (for status changes)
  const handleProductUpdate = (productId, updates) => {
    setProducts((prevProducts) => prevProducts.map((p) => (p.id === productId ? { ...p, ...updates } : p)));
    setFiltered((prevFiltered) => prevFiltered.map((p) => (p.id === productId ? { ...p, ...updates } : p)));
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Box>
        <Container maxWidth="lg">
          <Box mt={6}>
            <Box>
              <Box sx={{ display: "flex", gap: "10px", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="h1" gutterBottom>
                    Catalog
                  </Typography>
                </Box>
                <Button disabled={true} variant="contained" color="primary" component={RouterLink} to={`/site/${siteId}/product/add`}>
                  Add Item
                </Button>
              </Box>
              <Typography variant="body1" sx={{ alignSelf: "center", color: "text.secondary" }}>
                Browse & manage your catalog across all channels
              </Typography>
              <Box my={2}>
                <Tabs value={filter} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ borderBottom: "1px solid #ECECEC" }}>
                  <Tab value="active" label="Active" onClick={() => setFilter("active")} />
                  <Tab value="draft" label="Draft" onClick={() => setFilter("draft")} />
                </Tabs>
              </Box>
            </Box>
            {products.length > 0 && (
              <Box>
                <Box>
                  {products.length > 0 && (
                    <Box my={2}>
                      <Paper elevation={0}>
                        <Box py={0.75} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginRight: "5px" }}>
                          <ItemFilter
                            config={[
                              {
                                name: "category",
                                options: Array.from(new Set(products.flatMap((p) => p.category || [p.type?.toLowerCase()]).filter(Boolean)))
                              },
                              {
                                name: "channel",
                                options: ["online", "pos"]
                              },
                              {
                                name: "field",
                                options: Array.from(
                                  new Set(
                                    products
                                      .flatMap((p) =>
                                        p.variants
                                          ? Object.values(p.variants)
                                              .map((v) => v.name)
                                              .filter(Boolean)
                                          : []
                                      )
                                      .filter(Boolean)
                                  )
                                )
                              }
                            ]}
                            onChange={(filterUpdate) => {
                              setProductFilter({ ...productFilter, ...filterUpdate });
                            }}
                          />
                          <TextField
                            id="query"
                            value={query}
                            label=""
                            name="query"
                            variant="outlined"
                            size="small"
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Filter by Name"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment sx={{ paddingRight: "10px" }}>
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment>
                                  {query && (
                                    <IconButton onClick={(e) => setQuery("")}>
                                      <CloseOutlinedIcon />
                                    </IconButton>
                                  )}
                                </InputAdornment>
                              )
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "25px", // Adjust the value for desired roundness
                                "& fieldset": {
                                  borderColor: "primary.secondary"
                                },
                                "&:hover fieldset": {
                                  borderColor: "primary.main"
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "primary.main"
                                }
                              }
                            }}
                          />
                        </Box>
                      </Paper>
                    </Box>
                  )}

                  <Box my={4} sx={{ height: "auto", width: "100%" }}>
                    <DataGrid
                      rows={filtered.map((product, index) => ({
                        id: product.id || index,
                        ...product
                      }))}
                      columns={getColumns(siteId, isMobile, handleProductUpdate)}
                      disableColumnMenu={true}
                      autoHeight
                      getRowHeight={() => "auto"}
                      checkboxSelection
                      disableRowSelectionOnClick
                      stickyHeader
                      onRowSelectionModelChange={(newSelection) => {
                        setSelectedRows(newSelection);
                      }}
                      pageSize={10000}
                      pageSizeOptions={[10000]}
                      hideFooter={true}
                      slots={{
                        toolbar: () => <CustomFooterOnTop selectedRows={selectedRows} onBulkAction={handleBulkAction} />
                      }}
                      sx={{
                        "& .MuiDataGrid-cell": {
                          borderBottom: "1px solid #ECECEC",
                          paddingY: "10px"
                        },
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#ECECEC"
                        },
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                          backgroundColor: "#FFF" // A light gray color
                        },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                          backgroundColor: "#FFF" // Or any other color
                        },
                        // Target the focused cell
                        "& .MuiDataGrid-cell:focus": {
                          outline: "none"
                        },
                        // Target the focused cell within the row
                        "& .MuiDataGrid-cell:focus-within": {
                          outline: "none"
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
            {products.length === 0 && filter === "active" && (
              <Box my={6}>
                <Paper elevation={0}>
                  <Box px={2} py={2}>
                    <Typography variant="h5" gutterBottom>
                      Grow your Online Sales
                    </Typography>
                    <ul>
                      <li>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                          Diversify your business by offering everyday cakes, treats & desserts for pre-order.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                          Launch seasonal & limited time items to keep things exciting & build buzz around your business.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                          Leverage powerful rules to set lead time for each item to ensure you have sufficient time to prepare each item.
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                          Limit product availability between specific dates or a specific day of the week!
                        </Typography>
                      </li>
                    </ul>
                  </Box>
                </Paper>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    );
  }
}
